<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import AppModal from '@/components/common/AppModal.vue'
import AppButton from '@/components/common/AppButton.vue'
import LegalConsentCheckbox from '@/components/legal/LegalConsentCheckbox.vue'
import { useLegalConsentStore } from '@/stores/legalConsent'

/**
 * Modal único del consentimiento (ADR-SEC-013 local, HU-AUTH-006). Montado una
 * sola vez en `App.vue` y gobernado por `useLegalConsentStore`. Bloqueante: no
 * se cierra con Escape ni con clic fuera, y no tiene X. Tres modos:
 *
 * - `first`      «Antes de continuar» — nunca aceptó.
 * - `update`     «Actualizamos nuestros términos» — aceptó una versión anterior.
 * - `google-new` «Un último paso» — tiene sesión pero no `users/{uid}`: la cuenta
 *                se crea SÓLO al pulsar «Continuar».
 */

const consent = useLegalConsentStore()

const accepted = ref(false)
const showChanges = ref(false)

const isGoogleNew = computed(() => consent.mode === 'google-new')
const isUpdate = computed(() => consent.mode === 'update')

const title = computed(() => {
  if (consent.mode === 'google-new') return 'Un último paso'
  if (consent.mode === 'update') return 'Actualizamos nuestros términos'
  return 'Antes de continuar'
})

const greeting = computed(() => (consent.displayName ? `Hola, ${consent.displayName}.` : 'Hola.'))

const intro = computed(() => {
  if (consent.mode === 'google-new') {
    return consent.account?.isGoogle
      ? 'Vamos a crear tu cuenta de Quizoteca con tu cuenta de Google. Antes, confirma que aceptas cómo usamos tus datos.'
      : 'Vamos a terminar de crear tu cuenta de Quizoteca. Antes, confirma que aceptas cómo usamos tus datos.'
  }
  if (consent.mode === 'update') {
    return `${greeting.value} Cambiamos los Términos de uso y la Política de privacidad. Para seguir usando Quizoteca, revísalos y acéptalos.`
  }
  return `${greeting.value} Para seguir usando Quizoteca, revisa y acepta los Términos de uso y la Política de privacidad.`
})

const initial = computed(() => {
  const source = consent.account?.name || consent.account?.email || '?'
  return source.trim().charAt(0).toUpperCase() || '?'
})

// Cada apertura (o cada versión nueva tras TERMS_VERSION_OUTDATED) empieza desmarcada.
watch(
  () => [consent.required, consent.revision, consent.mode] as const,
  () => {
    accepted.value = false
    showChanges.value = false
  }
)

function onContinue() {
  if (!accepted.value || consent.submitting) return
  void consent.accept()
}

function onExit() {
  if (consent.submitting) return
  void consent.exit()
}
</script>

<template>
  <AppModal
    :show="consent.required"
    size="sm"
    :dismissable="false"
    labelled-by="legal-consent-title"
  >
    <div
      data-testid="legal-consent-modal"
      class="space-y-4"
    >
      <!-- Tarjeta de la cuenta de Google con la que se va a registrar -->
      <div
        v-if="isGoogleNew && consent.account?.isGoogle"
        data-testid="legal-consent-account"
        class="flex items-center gap-3 rounded-lg border border-border bg-slate-50 p-3"
      >
        <span
          class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-50 text-base font-semibold text-primary-500"
          aria-hidden="true"
        >{{ initial }}</span>
        <div class="min-w-0">
          <p
            v-if="consent.account?.name"
            class="truncate text-sm font-semibold text-slate-900"
          >
            {{ consent.account.name }}
          </p>
          <p class="truncate text-sm text-slate-500">
            {{ consent.account?.email }}
          </p>
        </div>
      </div>

      <div>
        <h2
          id="legal-consent-title"
          class="text-lg font-semibold text-slate-900"
        >
          {{ title }}
        </h2>
        <p class="mt-2 text-sm text-slate-600 leading-relaxed">
          {{ intro }}
        </p>
      </div>

      <div v-if="isUpdate">
        <button
          type="button"
          class="text-sm font-semibold text-primary-500 hover:underline"
          :aria-expanded="showChanges"
          @click="showChanges = !showChanges"
        >
          Ver qué cambió
        </button>
        <p
          v-if="showChanges"
          data-testid="legal-consent-changes"
          class="mt-2 rounded-lg bg-slate-50 p-3 text-sm text-slate-600 whitespace-pre-line"
        >
          {{ consent.current?.description || 'Sin descripción de los cambios.' }}
        </p>
      </div>

      <LegalConsentCheckbox
        id="legal-consent-modal-checkbox"
        v-model="accepted"
        :updated="isUpdate"
        :disabled="consent.submitting"
      />

      <p
        v-if="consent.error"
        role="alert"
        class="rounded-lg bg-danger-bg p-3 text-sm text-danger"
      >
        {{ consent.error }}
      </p>

      <div class="grid grid-cols-2 gap-3 pt-1">
        <AppButton
          variant="secondary"
          size="lg"
          :disabled="consent.submitting"
          @click="onExit"
        >
          {{ isGoogleNew ? 'Cancelar' : 'Salir' }}
        </AppButton>
        <AppButton
          variant="primary"
          size="lg"
          :disabled="!accepted"
          :loading="consent.submitting"
          @click="onContinue"
        >
          Continuar
        </AppButton>
      </div>
    </div>
  </AppModal>
</template>
